// Recibo de entrega (remisión) imprimible — /entrega/:id/recibo
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { theme } from '../styles/theme';

const BRAND = '#0e0e0e';
const ACCENT = '#57FF9A';

type Del = {
  id: string; folio: string | null; carrier: string | null; tracking: string | null; note: string | null;
  delivered_at: string | null; status: string; created_at: string;
  scheduled_for: string | null; address: string | null; contact: string | null; received_by: string | null; instructions: string | null;
  order_number: string | null; customer_name: string | null;
  items: { sku: string | null; description: string | null; qty: number }[];
};

export function ReciboEntrega() {
  const { id } = useParams();
  const [d, setD] = useState<Del | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!id) return;
    (async () => {
      const { data } = await supabase
        .from('customer_deliveries')
        .select('id, folio, carrier, tracking, note, delivered_at, status, created_at, scheduled_for, address, contact, received_by, instructions, orders(order_number, customers(business_name)), customer_delivery_items(qty, order_items(sku, description))')
        .eq('id', id).maybeSingle();
      if (data) {
        const x = data as any;
        setD({
          id: x.id, folio: x.folio, carrier: x.carrier, tracking: x.tracking, note: x.note,
          delivered_at: x.delivered_at, status: x.status, created_at: x.created_at,
          scheduled_for: x.scheduled_for, address: x.address, contact: x.contact, received_by: x.received_by, instructions: x.instructions,
          order_number: x.orders?.order_number ?? null, customer_name: x.orders?.customers?.business_name ?? null,
          items: (x.customer_delivery_items ?? []).map((ci: any) => ({ sku: ci.order_items?.sku ?? null, description: ci.order_items?.description ?? null, qty: Number(ci.qty) })),
        });
      }
      setLoading(false);
    })();
  }, [id]);

  if (loading) return <div style={{ padding: 40, fontFamily: theme.font.sans }}>Cargando recibo…</div>;
  if (!d) return <div style={{ padding: 40, fontFamily: theme.font.sans }}>Recibo no encontrado</div>;

  const totalQty = d.items.reduce((a, x) => a + x.qty, 0);
  const fecha = new Date(d.delivered_at ?? (d.scheduled_for ? d.scheduled_for + 'T00:00:00' : d.created_at)).toLocaleDateString('es-MX', { day: '2-digit', month: 'long', year: 'numeric' });

  return (
    <>
      <style>{`
        * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
        @media print { @page { size: letter; margin: 14mm; } .no-print { display: none !important; } body { background: #fff !important; } }
      `}</style>
      <div style={{ fontFamily: theme.font.sans, color: BRAND, maxWidth: 780, margin: '0 auto', padding: 24 }}>
        <div className="no-print" style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginBottom: 12 }}>
          <button onClick={() => window.print()} style={{ background: ACCENT, color: BRAND, border: 'none', padding: '10px 18px', fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: theme.font.sans }}>🖨 Imprimir / PDF</button>
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', borderBottom: `3px solid ${BRAND}`, paddingBottom: 14, marginBottom: 18 }}>
          <div>
            <div style={{ fontFamily: theme.font.head, fontSize: 30, fontWeight: 700, letterSpacing: 1 }}>NULED</div>
            <div style={{ fontSize: 11, color: '#666', marginTop: 2 }}>Iluminación LED · nuled.mx · ventas@nuled.mx</div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: 16, fontWeight: 700, letterSpacing: 1 }}>RECIBO DE ENTREGA</div>
            <div style={{ fontSize: 12, color: '#666', marginTop: 4 }}>Folio: <b style={{ color: BRAND }}>{d.folio ?? '—'}</b></div>
            <div style={{ fontSize: 12, color: '#666' }}>Fecha: {fecha}</div>
            {d.status !== 'delivered' && <div style={{ marginTop: 6, display: 'inline-block', background: d.status === 'cancelled' ? '#FEE2E2' : '#FEF3C7', color: d.status === 'cancelled' ? '#B91C1C' : '#9A7B00', fontSize: 10, fontWeight: 700, padding: '2px 8px' }}>{d.status === 'draft' ? 'BORRADOR' : d.status === 'scheduled' ? 'EN TRÁNSITO' : 'CANCELADA'}</div>}
          </div>
        </div>

        <div style={{ display: 'flex', gap: 32, rowGap: 12, flexWrap: 'wrap', marginBottom: 18, fontSize: 13 }}>
          <div><div style={{ fontSize: 10, color: '#888', textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 700 }}>Cliente</div><div style={{ fontWeight: 600 }}>{d.customer_name ?? '—'}</div></div>
          <div><div style={{ fontSize: 10, color: '#888', textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 700 }}>Pedido</div><div style={{ fontFamily: theme.font.mono }}>{d.order_number ?? '—'}</div></div>
          {d.address && <div><div style={{ fontSize: 10, color: '#888', textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 700 }}>Entregar en</div><div>{d.address}</div></div>}
          {d.contact && <div><div style={{ fontSize: 10, color: '#888', textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 700 }}>Contacto</div><div>{d.contact}</div></div>}
          {(d.carrier || d.tracking) && <div><div style={{ fontSize: 10, color: '#888', textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 700 }}>Envío</div><div>{d.carrier ?? ''}{d.tracking ? ` · ${d.tracking}` : ''}</div></div>}
        </div>

        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ background: BRAND, color: '#fff' }}>
              <th style={{ textAlign: 'left', padding: '8px 10px', fontSize: 11 }}>SKU</th>
              <th style={{ textAlign: 'left', padding: '8px 10px', fontSize: 11 }}>Descripción</th>
              <th style={{ textAlign: 'right', padding: '8px 10px', fontSize: 11, width: 90 }}>Cantidad</th>
            </tr>
          </thead>
          <tbody>
            {d.items.map((it, i) => (
              <tr key={i} style={{ borderBottom: '1px solid #e5e5e5' }}>
                <td style={{ padding: '8px 10px', fontFamily: theme.font.mono, fontSize: 11 }}>{it.sku}</td>
                <td style={{ padding: '8px 10px' }}>{it.description}</td>
                <td style={{ padding: '8px 10px', textAlign: 'right', fontWeight: 700 }}>{it.qty}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr><td colSpan={2} style={{ padding: '10px', textAlign: 'right', fontWeight: 700 }}>Total de piezas</td><td style={{ padding: '10px', textAlign: 'right', fontWeight: 700, fontSize: 15 }}>{totalQty}</td></tr>
          </tfoot>
        </table>

        {d.instructions && <div style={{ marginTop: 16, fontSize: 12, color: '#555' }}><b>Instrucciones de entrega:</b> {d.instructions}</div>}
        {d.note && <div style={{ marginTop: 16, fontSize: 12, color: '#555' }}><b>Nota:</b> {d.note}</div>}

        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 60, gap: 40 }}>
          <div style={{ flex: 1, borderTop: '1px solid #999', paddingTop: 6, textAlign: 'center', fontSize: 11, color: '#666' }}>Entregó (NULED)</div>
          <div style={{ flex: 1, borderTop: '1px solid #999', paddingTop: 6, textAlign: 'center', fontSize: 11, color: '#666' }}>Recibió (Cliente) — nombre y firma{d.received_by ? `: ${d.received_by}` : ''}</div>
        </div>
      </div>
    </>
  );
}
